import type { GanttFeature } from '@/components/kibo-ui/gantt'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import { Card as GanttCard } from '@/components/kibo-ui/gantt/card'
import { ModuleWrapper } from '@/components'
import { DriverActivityType } from '@/modules/activities/consts'
import { PageType } from '@/modules/common/consts'
import { format } from '@/utils/date-fns'
import {
  useAdmin,
  useBreadcrumbOverride,
  useOnErrorAuto,
  useParam,
} from '@/utils/hooks'
import { durationAsSecondsToString } from '@/utils/time'
import { useActivityColor } from '@/utils'
import { useMutation, useQuery } from '@apollo/client'
import { Loader, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  addHours,
  addMinutes,
  differenceInMinutes,
  isSameDay,
  max,
  min,
  parseISO,
  startOfHour,
} from 'date-fns'
import { Minus, Plus } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import { useWorkInfringementCategoryTranslations } from '../category-translations'
import {
  WorkDayActions,
  WorkDaySummary,
  WronglyGeneratedPrompt,
} from '../components'
import {
  InfringementActionLogCard,
  infringementActionsPlacement,
} from '@/modules/infringements/components/infringement-follow-up'
import { PATCH_WORK_INFRINGEMENT_WRONGLY_GENERATED } from '../mutations'
import { WORK_DAY_INFRINGEMENT_QUERY } from '../queries'
import { getWorkDayCategoryDisplayIdentifier } from '../work-day-category-labels'
import {
  isWorkDaySegmentType,
  type SegmentType,
  WORK_DAY_SEGMENT_TYPES,
  type WorkDaySegment,
} from './work-day-segment-types'

const ROWS = WORK_DAY_SEGMENT_TYPES

const COLORS = {
  danger: '#DC2626',
  ink: '#0F1F3A',
  muted: '#5C6B85',
  faint: '#94A3B8',
}

const GANTT_COLUMN_WIDTH_PX = 5000
const EMPTY_WORK_DAY_SEGMENTS: WorkDaySegment[] = []

export const WorkDayInfringement: React.FC = () => {
  const categoryTranslations = useWorkInfringementCategoryTranslations()
  const translations = useTranslate({
    title: 'infringements.work-day-infringement',
    rule: 'infringements.rule',
    basis: 'common.basis',
    driver: 'common.driver',
    legalBasis: 'infringements.legal-basis',
    measuredValue: 'infringements.measured-value',
    secondaryValue: 'infringements.secondary-value',
    period: 'common.period',
    archivedBasis: 'infringements.archived-basis',
    archivedDate: 'infringements.archived-date',
    activityTimeline: 'infringements.activity-timeline',
    evaluationWindow: 'infringements.evaluation-window',
    detectedAt: 'infringements.detected-at',
    recordedValue: 'infringements.recorded-value',
    work: 'common.work',
    rest: 'common.rest',
    availability: 'common.availability',
    unknown: 'common.unknown',
    workDayCategoryOverwork: 'infringements.work-day-category.overwork',
    workDayCategoryPause: 'infringements.work-day-category.pause',
    wronglyGenerated: 'infringements.wrongly-generated',
    wronglyGeneratedNotice: 'infringements.wrongly-generated-notice',
    wronglyGeneratedExplanation: 'infringements.wrongly-generated-explanation',
    markWronglyGenerated: 'infringements.mark-wrongly-generated',
    unmarkWronglyGenerated: 'infringements.unmark-wrongly-generated',
  })
  const infringementId = useParam('infringementId')
  const navigate = useNavigate()
  const onErrorAuto = useOnErrorAuto()
  const getActivityColor = useActivityColor()
  const isAdmin = useAdmin()
  const addPrompt = usePrompt()
  const [ganttZoom, setGanttZoom] = useState(100)
  const ganttWrapRef = useRef<HTMLDivElement | null>(null)

  const { data, loading } = useQuery(WORK_DAY_INFRINGEMENT_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: infringementId,
    },
    onCompleted(data) {
      if (!data.workDayInfringement) void navigate('/infringements')
    },
    onError: onErrorAuto(),
  })

  const [patchWronglyGenerated, { loading: wronglyGeneratedLoading }] =
    useMutation(PATCH_WORK_INFRINGEMENT_WRONGLY_GENERATED, {
      awaitRefetchQueries: true,
      refetchQueries: ['AllCombinedInfringements'],
      onError: onErrorAuto(),
    })

  const infringement = data?.workDayInfringement
  const periodStartDate = infringement?.periodStartDate
  const periodEndDate = infringement?.periodEndDate
  const infringementTimestamp = infringement?.infringementTimestamp
  const infringementSegments = infringement?.segments

  useBreadcrumbOverride(
    infringementId,
    infringementTimestamp ? format(new Date(infringementTimestamp), 'PP') : null
  )

  const periodStart = useMemo(
    () => (periodStartDate ? parseISO(periodStartDate) : new Date()),
    [periodStartDate]
  )
  const periodEnd = useMemo(
    () => (periodEndDate ? parseISO(periodEndDate) : new Date()),
    [periodEndDate]
  )
  const segments = useMemo(
    () => infringementSegments ?? EMPTY_WORK_DAY_SEGMENTS,
    [infringementSegments]
  )
  const rowLabels = useMemo<Record<SegmentType, string>>(
    () => ({
      WORK: translations.work,
      REST: translations.rest,
      INFERRED_REST: `${translations.rest} (${translations.unknown})`,
      AVAILABLE: translations.availability,
      UNKNOWN: translations.unknown,
    }),
    [
      translations.availability,
      translations.rest,
      translations.unknown,
      translations.work,
    ]
  )
  const rowColors = useMemo<Record<SegmentType, string>>(() => {
    const workColor = getActivityColor({
      activityType: DriverActivityType.WORK,
    })
    const restColor = getActivityColor({
      activityType: DriverActivityType.REST,
    })
    const availabilityColor = getActivityColor({
      activityType: DriverActivityType.AVAILABLE,
    })

    return {
      WORK: workColor,
      REST: restColor,
      INFERRED_REST: COLORS.faint,
      AVAILABLE: availabilityColor,
      UNKNOWN: COLORS.faint,
    }
  }, [getActivityColor])

  const ganttRange = useMemo(() => {
    const dates = [
      periodStart,
      periodEnd,
      ...segments.flatMap(segment => [
        parseISO(segment.datetimeStart),
        parseISO(segment.datetimeEnd),
      ]),
    ]

    return {
      datetimeStart: addHours(min(dates), -1),
      datetimeEnd: addHours(max(dates), 1),
    }
  }, [periodEnd, periodStart, segments])

  const ganttPxPerMinute = ((ganttZoom / 2400) * GANTT_COLUMN_WIDTH_PX) / 60

  const infringementAt = useMemo(
    () =>
      infringementTimestamp ? parseISO(infringementTimestamp) : new Date(),
    [infringementTimestamp]
  )
  const toTimelineLeft = useCallback(
    (date: Date) =>
      differenceInMinutes(date, ganttRange.datetimeStart) * ganttPxPerMinute,
    [ganttPxPerMinute, ganttRange.datetimeStart]
  )
  const tickLeft = useMemo(
    () => toTimelineLeft(infringementAt),
    [infringementAt, toTimelineLeft]
  )

  const minorStepMin =
    ganttZoom >= 400 ? 5 : ganttZoom >= 200 ? 15 : ganttZoom >= 100 ? 30 : 0
  const minorTicks = useMemo(() => {
    if (minorStepMin === 0) return []

    const ticks: { at: Date; left: number }[] = []
    let current = startOfHour(ganttRange.datetimeStart)
    if (current < ganttRange.datetimeStart) current = addHours(current, 1)

    while (current <= ganttRange.datetimeEnd) {
      for (let minutes = minorStepMin; minutes < 60; minutes += minorStepMin) {
        const at = addMinutes(current, minutes)
        if (at > ganttRange.datetimeEnd) break
        ticks.push({ at, left: toTimelineLeft(at) })
      }
      current = addHours(current, 1)
    }
    return ticks
  }, [
    ganttRange.datetimeEnd,
    ganttRange.datetimeStart,
    minorStepMin,
    toTimelineLeft,
  ])

  const featuresByRow = useMemo(
    () => buildFeaturesByRow(segments, rowLabels, rowColors),
    [rowColors, rowLabels, segments]
  )
  const rowDurationMinutes = useMemo(
    () =>
      ROWS.reduce<Record<SegmentType, number>>(
        (acc, row) => ({
          ...acc,
          [row]: featuresByRow[row].reduce(
            (sum, feature) =>
              sum + differenceInMinutes(feature.endAt, feature.startAt),
            0
          ),
        }),
        { WORK: 0, REST: 0, INFERRED_REST: 0, AVAILABLE: 0, UNKNOWN: 0 }
      ),
    [featuresByRow]
  )

  useEffect(() => {
    const wrap = ganttWrapRef.current
    if (!wrap) return

    const ganttEl = wrap.querySelector<HTMLDivElement>('.gantt')
    if (!ganttEl) return

    const frame = requestAnimationFrame(() => {
      const sidebar = ganttEl.querySelector<HTMLElement>(
        '[data-roadmap-ui="gantt-sidebar"]'
      )
      const sidebarWidth = sidebar?.getBoundingClientRect().width ?? 0
      const target = Math.max(
        0,
        tickLeft + sidebarWidth - ganttEl.clientWidth / 2
      )
      ganttEl.scrollLeft = target
    })

    return () => cancelAnimationFrame(frame)
  }, [tickLeft])

  async function handleToggleWronglyGenerated() {
    if (!infringement) return

    if (infringement.wronglyGenerated) {
      await patchWronglyGenerated({
        variables: { id: infringement.id, wronglyGenerated: false },
      })
      return
    }

    const { data: explanation } = await addPrompt<string | null>(resolve => (
      <WronglyGeneratedPrompt onSubmit={resolve} />
    ))
    if (!explanation) return

    await patchWronglyGenerated({
      variables: {
        id: infringement.id,
        wronglyGenerated: true,
        explanation,
      },
    })
  }

  if (loading && !infringement) return <Loader.Spinner />
  if (!infringement) return null

  const category = infringement.workInfringementCategory
  const displayIdentifier = getWorkDayCategoryDisplayIdentifier(
    category.identifier,
    {
      overwork: translations.workDayCategoryOverwork,
      pause: translations.workDayCategoryPause,
    }
  )
  const measuredValue = formatHours(infringement.reasonValue)
  const secondaryValue = formatHours(infringement.secondaryReasonValue)
  const periodText = `${format(periodStart, 'PP HH:mm')} - ${format(
    periodEnd,
    'PP HH:mm'
  )}`

  return (
    <ModuleWrapper
      title={translations.title}
      page={PageType.INFRINGEMENTS}
      {...infringementActionsPlacement(
        <WorkDayActions
          infringement={infringement}
          canMarkWronglyGenerated={isAdmin}
          wronglyGeneratedLoading={wronglyGeneratedLoading}
          onToggleWronglyGenerated={() => void handleToggleWronglyGenerated()}
        />
      )}
    >
      <div className="w-full space-y-6">
        <WorkDaySummary
          infringement={infringement}
          displayIdentifier={displayIdentifier}
          title={categoryTranslations.title(
            category.identifier,
            category.title
          )}
          description={categoryTranslations.description(
            category.identifier,
            category.description
          )}
          measuredValue={measuredValue}
          secondaryValue={
            infringement.secondaryReasonValue !== null ? secondaryValue : null
          }
          periodText={periodText}
        />

        <section className="overflow-hidden rounded-2xl bg-white shadow-card">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-0.5">
              <h3 className="text-lg font-semibold text-stone-900">
                {translations.activityTimeline}
              </h3>
              <span className="text-xs text-stone-500">
                {translations.evaluationWindow}:{' '}
                {format(periodStart, 'PP HH:mm')} →{' '}
                {format(periodEnd, 'PP HH:mm')}
              </span>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-stone-300">
              <button
                className="flex h-8 w-8 items-center justify-center disabled:opacity-40"
                disabled={ganttZoom <= 25}
                onClick={() => setGanttZoom(zoom => Math.max(25, zoom - 25))}
                style={{ color: COLORS.muted }}
                type="button"
              >
                <Minus size={14} />
              </button>
              <span
                className="min-w-[36px] text-center text-[11px] font-semibold"
                style={{ color: COLORS.ink }}
              >
                {ganttZoom}%
              </span>
              <button
                className="flex h-8 w-8 items-center justify-center disabled:opacity-40"
                disabled={ganttZoom >= 400}
                onClick={() => setGanttZoom(zoom => Math.min(400, zoom + 25))}
                style={{ color: COLORS.muted }}
                type="button"
              >
                <Plus size={14} />
              </button>
            </div>
          </header>

          <div
            ref={ganttWrapRef}
            className="relative w-full max-w-full px-5 pt-4 pb-4 sm:px-6"
          >
            <GanttProvider
              className="border pb-5"
              range="daily"
              zoom={ganttZoom}
              datetimeStart={ganttRange.datetimeStart}
              datetimeEnd={ganttRange.datetimeEnd}
              cursorFollowTooltips
            >
              <GanttSidebar
                compact={isMobile}
                className="!w-28 sm:!w-56 md:!w-64"
              >
                {ROWS.map(row => (
                  <GanttSidebarItem
                    key={row}
                    feature={{
                      id: row,
                      name: rowLabels[row],
                      startAt: featuresByRow[row][0]?.startAt ?? periodStart,
                      endAt:
                        featuresByRow[row][featuresByRow[row].length - 1]
                          ?.endAt ?? periodEnd,
                      duration: rowDurationMinutes[row],
                    }}
                  />
                ))}
              </GanttSidebar>
              <GanttTimeline>
                <GanttHeader />
                {minorTicks.length > 0 && (
                  <div
                    className="pointer-events-none absolute left-0 z-10"
                    style={{
                      top: 'calc(var(--gantt-header-height) - 18px)',
                      height: 14,
                    }}
                  >
                    {minorTicks.map(({ at, left }) => (
                      <div
                        className="absolute -translate-x-1/2 text-[10px]"
                        key={at.getTime()}
                        style={{ left, color: COLORS.faint }}
                      >
                        {format(at, 'HH:mm')}
                      </div>
                    ))}
                  </div>
                )}

                <div
                  className="absolute inset-y-0 z-0"
                  style={{
                    left: toTimelineLeft(periodStart),
                    width:
                      toTimelineLeft(periodEnd) - toTimelineLeft(periodStart),
                  }}
                >
                  <GanttCard
                    className="h-full w-full border-0 p-0"
                    cursorFollow
                    style={{
                      background: 'rgba(15, 31, 58, 0.04)',
                      borderLeft: `1px dashed ${COLORS.faint}`,
                      borderRight: `1px dashed ${COLORS.faint}`,
                    }}
                    tooltipContent={
                      <div className="flex flex-col gap-0.5 text-left">
                        <div className="font-semibold">
                          {translations.evaluationWindow}
                        </div>
                        {isSameDay(periodStart, periodEnd) ? (
                          <>
                            <div className="opacity-80">
                              {format(periodStart, 'PP')}
                            </div>
                            <div className="whitespace-nowrap">
                              {format(periodStart, 'HH:mm')} -{' '}
                              {format(periodEnd, 'HH:mm')}
                            </div>
                          </>
                        ) : (
                          <>
                            <div>{format(periodStart, 'PP HH:mm')}</div>
                            <div>{format(periodEnd, 'PP HH:mm')}</div>
                          </>
                        )}
                        <div className="mt-1 opacity-80">
                          {formatDuration(
                            differenceInMinutes(periodEnd, periodStart)
                          )}
                        </div>
                      </div>
                    }
                  />
                </div>

                <GanttFeatureList>
                  {ROWS.map(row => (
                    <GanttFeatureRow
                      key={row}
                      className="min-w-full"
                      features={featuresByRow[row]}
                      handleViewFeature={() => void 0}
                    />
                  ))}
                </GanttFeatureList>

                <div
                  className="absolute z-20"
                  style={{
                    top: 'var(--gantt-header-height)',
                    bottom: 0,
                    left: tickLeft - 5,
                    width: 12,
                  }}
                >
                  <GanttCard
                    className="h-full w-full border-0 bg-transparent p-0"
                    cursorFollow
                    tooltipContent={
                      <div className="flex flex-col gap-1 text-left">
                        <div className="font-semibold">
                          {displayIdentifier}.{' '}
                          {categoryTranslations.title(
                            category.identifier,
                            category.title
                          )}
                        </div>
                        <div className="opacity-80">
                          {translations.detectedAt}
                        </div>
                        <div>{format(infringementAt, 'PP HH:mm')}</div>
                      </div>
                    }
                  >
                    <div
                      className="pointer-events-none absolute inset-y-0"
                      style={{
                        left: 5,
                        width: 2,
                        background: COLORS.danger,
                        borderRadius: 1,
                      }}
                    />
                  </GanttCard>
                </div>
              </GanttTimeline>
            </GanttProvider>
          </div>
        </section>

        <InfringementActionLogCard actionLogs={infringement.actionLogs} />
      </div>
    </ModuleWrapper>
  )
}

function buildFeaturesByRow(
  segments: readonly WorkDaySegment[],
  rowLabels: Record<SegmentType, string>,
  rowColors: Record<SegmentType, string>
) {
  return segments.reduce<Record<SegmentType, GanttFeature[]>>(
    (acc, segment) => {
      if (!isWorkDaySegmentType(segment.segmentType)) return acc

      const row = segment.segmentType
      const startAt = parseISO(segment.datetimeStart)
      const endAt = parseISO(segment.datetimeEnd)
      const durationMinutes = differenceInMinutes(endAt, startAt)

      if (durationMinutes <= 0) return acc

      acc[row].push({
        id: segment.id,
        name: rowLabels[row],
        startAt,
        endAt,
        duration: durationMinutes,
        group: {
          id: row,
          name: rowLabels[row],
          color: rowColors[row],
        },
        tooltipContent: (
          <div className="flex flex-col gap-1 text-left">
            <div className="font-semibold">{rowLabels[row]}</div>
            <div>
              {format(startAt, 'PP HH:mm')} - {format(endAt, 'PP HH:mm')}
            </div>
            <div>{formatDuration(durationMinutes)}</div>
          </div>
        ),
      })

      return acc
    },
    { WORK: [], REST: [], INFERRED_REST: [], AVAILABLE: [], UNKNOWN: [] }
  )
}

function formatHours(value: number | null | undefined) {
  if (value === null || value === undefined) return '-'

  return formatDuration(Math.round(value * 60))
}

function formatDuration(minutes: number) {
  return durationAsSecondsToString(minutes * 60)
}
